import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Button, cn, Select } from "@tellescope/design-system";
import { getDefaultView } from "./constants";
import { ViewSelectItem } from "./ViewSelectItem";
import { EllipsisVertical, Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Input,
  Skeleton,
  withKey,
} from "@tellescope/ui";
import {
  TellescopeModelFromName,
  useAuth,
  usePermissionQuery,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { ViewConfigForm } from "../dialogs/ViewConfigForm";
import { toast } from "sonner";
import { formatName } from "@tellescope/utils";
import { HeaderMenuItem, HeaderMenuItemProps } from "./HeaderMenuItem";

export interface ContactDetailsHeaderProps {
  enduserId: string;
  views: TellescopeModelFromName<"custom_dashboards">[];
  viewsAreLoading: boolean;
  selectedViewId: string | undefined;
  setSelectedViewId: Dispatch<SetStateAction<string | undefined>>;
  defaultViewId?: string;
  closeSidebar: () => void;
  menuItems?: HeaderMenuItemProps[];
  className?: string;
}

export const ContactDetailsHeader = withKey(
  ({
    enduserId,
    views,
    viewsAreLoading,
    selectedViewId,
    setSelectedViewId,
    defaultViewId,
    closeSidebar,
    menuItems = [],
    className,
  }: ContactDetailsHeaderProps) => {
    const { t } = useTranslation("contacts");
    const { currentUser, isAdmin } = useAuth();
    const [configuringViewId, setConfiguringViewId] = useState<string | null>();
    const [newViewName, setNewViewName] = useState<string>();
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    const openConfigDialog = (viewId: string | null) => {
      setIsSelectOpen(false);
      setConfiguringViewId(viewId);
    };

    const { data: enduser, isLoading: enduserIsLoading } = useTellescopeQuery({
      model: "endusers",
      method: "getOne",
      request: enduserId,
      enabled: !!enduserId,
    });

    const createViewMutation = useTellescopeMutation({
      model: "custom_dashboards",
      method: "createOne",
    });

    const isLoading = enduserIsLoading || viewsAreLoading;

    const {
      create: { access: canCreate },
    } = usePermissionQuery({
      model: "custom_dashboards",
      actions: ["create"],
    });
    const hasVisibleMenuItems = menuItems.some((item) => !item.hidden);

    const orderedViews = useMemo(
      () => [...views].sort((a, b) => Number(b.id === defaultViewId) - Number(a.id === defaultViewId)),
      [defaultViewId, views]
    );

    return (
      <section className={cn("flex items-center w-full gap-2", className)}>
        {isLoading ? (
          <Skeleton className="grow h-4" />
        ) : (
          <>
            <Select
              name="contact-details-select-view"
              value={selectedViewId}
              onValueChange={setSelectedViewId}
              open={isSelectOpen}
              onOpenChange={(open) => {
                setIsSelectOpen(open);
                if (!open) setNewViewName(undefined);
              }}
            >
              <Select.Trigger>
                <Select.Value />
              </Select.Trigger>
              <Select.Content
                indicatorSide="left"
                className="min-w-[250px]"
                onEscapeKeyDown={(e) => {
                  if (document.activeElement instanceof HTMLInputElement) {
                    e.preventDefault();
                  }
                }}
              >
                {orderedViews.map((view, _, { length }) => (
                  <ViewSelectItem
                    key={view.id}
                    view={view}
                    isLastView={length === 1}
                    isDefault={view.id === defaultViewId}
                    onDeleted={(deletedViewId) =>
                      setSelectedViewId((current) => (current === deletedViewId ? undefined : current))
                    }
                    onEdit={openConfigDialog}
                  />
                ))}
                {canCreate && (
                  <>
                    <Select.Separator />
                    {newViewName === undefined ? (
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        size="sm"
                        onClick={() => (isAdmin ? openConfigDialog(null) : setNewViewName(""))}
                        tabIndex={0}
                      >
                        <Plus />
                        {t("Add view")}
                      </Button>
                    ) : (
                      <Input
                        value={newViewName}
                        onChange={(e) => setNewViewName(e.target.value)}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            const defaultView = getDefaultView();
                            return createViewMutation.mutateAsync(
                              {
                                title: newViewName,
                                type: defaultView.type,
                                blocks: defaultView.blocks,
                                userIds: [currentUser.id],
                              },
                              {
                                onSuccess: () => {
                                  setNewViewName(undefined);
                                },
                                onError: () => {
                                  toast.error(t("Something went wrong... Please try again"));
                                },
                              }
                            );
                          } else if (e.key === "Escape") {
                            setNewViewName(undefined);
                          }
                        }}
                        autoFocus
                      />
                    )}
                  </>
                )}
              </Select.Content>
            </Select>
            <h3 className="grow truncate text-right text-sm">{enduser ? formatName(enduser) : ""}</h3>
            {!!hasVisibleMenuItems && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button iconOnly variant="ghost">
                    <EllipsisVertical />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {menuItems.map((item, i) => (
                    <HeaderMenuItem key={item.label + i} {...item} />
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        )}
        <Button iconOnly variant="ghost" onClick={closeSidebar}>
          <X />
        </Button>
        <Dialog
          open={configuringViewId !== undefined}
          onOpenChange={(open) => !open && setConfiguringViewId(undefined)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{configuringViewId ? t("Edit view") : t("Add view")}</DialogTitle>
            </DialogHeader>
            <ViewConfigForm
              key={configuringViewId ?? "new"}
              viewId={configuringViewId ?? undefined}
              onSubmitSuccess={(viewId) => {
                setConfiguringViewId(undefined);
                setSelectedViewId(viewId);
              }}
              onSubmitError={() => toast.error(t("Something went wrong... Please try again"))}
              onCancel={() => setConfiguringViewId(undefined)}
            />
          </DialogContent>
        </Dialog>
      </section>
    );
  },
  ({ enduserId, selectedViewId }) => `${enduserId}-${selectedViewId}-contact-details-sidebar`
);
